import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export let lastModified = undefined;
export let frontmatter = {
  "title": "Turborepo API reference",
  "description": "Learn about Turborepo's APIs using the reference."
};
export let structuredData = {
  "contents": [{
    "heading": "",
    "content": "Turborepo's API reference is broken up into the following sections:"
  }, {
    "heading": "flag-syntax",
    "content": "Options that require a value can be passed with an equals sign, using quotes when spaces are needed."
  }, {
    "heading": "--color",
    "content": "Forces the use of color, even in non-interactive terminals. This is useful for enabling color output in CI environments like GitHub Actions that have support for rendering color."
  }, {
    "heading": "--no-color",
    "content": "Suppresses color in terminal output, even in interactive terminals."
  }, {
    "heading": "--no-update-notifier",
    "content": "Disables the update notification. This notification will be automatically disabled when running in CI environments, but can also be disabled manually via this flag."
  }, {
    "heading": "--no-update-notifier",
    "content": "Alternatively, you can disable the notification using the TURBO_NO_UPDATE_NOTIFIER environment variable."
  }],
  "headings": [{
    "id": "configuration",
    "content": "Configuration"
  }, {
    "id": "commands",
    "content": "Commands"
  }, {
    "id": "packages",
    "content": "Packages"
  }, {
    "id": "flag-syntax",
    "content": "Flag syntax"
  }, {
    "id": "global-flags",
    "content": "Global flags"
  }, {
    "id": "--color",
    "content": "--color"
  }, {
    "id": "--no-color",
    "content": "--no-color"
  }, {
    "id": "--no-update-notifier",
    "content": "--no-update-notifier"
  }]
};
import {Card, Cards} from '#components/card';
export const toc = [{
  depth: 2,
  url: "#configuration",
  title: _jsx(_Fragment, {
    children: "Configuration"
  })
}, {
  depth: 2,
  url: "#commands",
  title: _jsx(_Fragment, {
    children: "Commands"
  })
}, {
  depth: 2,
  url: "#packages",
  title: _jsx(_Fragment, {
    children: "Packages"
  })
}, {
  depth: 2,
  url: "#flag-syntax",
  title: _jsx(_Fragment, {
    children: "Flag syntax"
  })
}, {
  depth: 2,
  url: "#global-flags",
  title: _jsx(_Fragment, {
    children: "Global flags"
  })
}, {
  depth: 3,
  url: "#--color",
  title: _jsx(_Fragment, {
    children: _jsx("code", {
      children: "--color"
    })
  })
}, {
  depth: 3,
  url: "#--no-color",
  title: _jsx(_Fragment, {
    children: _jsx("code", {
      children: "--no-color"
    })
  })
}, {
  depth: 3,
  url: "#--no-update-notifier",
  title: _jsx(_Fragment, {
    children: _jsx("code", {
      children: "--no-update-notifier"
    })
  })
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    h2: "h2",
    h3: "h3",
    p: "p",
    pre: "pre",
    span: "span",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "Turborepo's API reference is broken up into the following sections:"
    }), "\n", _jsx(_components.h2, {
      id: "configuration",
      children: "Configuration"
    }), "\n", _jsxs(Cards, {
      children: [_jsx(Card, {
        title: "Configuring turbo.json",
        href: "/docs/reference/configuration",
        description: "Configure the behavior of `turbo`."
      }), _jsx(Card, {
        title: "Package Configurations",
        href: "/docs/reference/package-configurations",
        description: "Create configurations specific to a package."
      }), _jsx(Card, {
        title: "System environment variables",
        href: "/docs/reference/system-environment-variables",
        description: "Change `turbo`'s behavior using environment variables."
      }), _jsx(Card, {
        title: "File glob specification",
        href: "/docs/reference/globs",
        description: "Define files for `turbo` to use."
      })]
    }), "\n", _jsx(_components.h2, {
      id: "commands",
      children: "Commands"
    }), "\n", _jsxs(Cards, {
      children: [_jsx(Card, {
        title: "run",
        href: "/docs/reference/run",
        description: "Run tasks using `turbo`."
      }), _jsx(Card, {
        title: "watch",
        href: "/docs/reference/watch",
        description: "Dependency-aware, single-process task watcher."
      }), _jsx(Card, {
        title: "prune",
        href: "/docs/reference/prune",
        description: "Create partial monorepos for target workspaces."
      }), _jsx(Card, {
        title: "boundaries",
        href: "/docs/reference/boundaries",
        description: "Enforce best practices in your monorepo."
      }), _jsx(Card, {
        title: "ls",
        href: "/docs/reference/ls",
        description: "List packages in your monorepo."
      }), _jsx(Card, {
        title: "query",
        href: "/docs/reference/query",
        description: "Run GraphQL queries against your monorepo."
      }), _jsx(Card, {
        title: "generate",
        href: "/docs/reference/generate",
        description: "Extend your Turborepo with new apps and packages."
      }), _jsx(Card, {
        title: "login",
        href: "/docs/reference/login",
        description: "Log in to your Remote Cache provider."
      }), _jsx(Card, {
        title: "logout",
        href: "/docs/reference/logout",
        description: "Log out of your Remote Cache provider."
      }), _jsx(Card, {
        title: "link",
        href: "/docs/reference/link",
        description: "Link your repository to Remote Cache."
      }), _jsx(Card, {
        title: "unlink",
        href: "/docs/reference/unlink",
        description: "Disconnect your repository from Remote Cache."
      }), _jsx(Card, {
        title: "scan",
        href: "/docs/reference/scan",
        description: "Enable the fastest defaults for your Turborepo."
      }), _jsx(Card, {
        title: "bin",
        href: "/docs/reference/bin",
        description: "Get the path to the `turbo` binary."
      }), _jsx(Card, {
        title: "telemetry",
        href: "/docs/reference/telemetry",
        description: "Manage telemetry for the machine."
      })]
    }), "\n", _jsx(_components.h2, {
      id: "packages",
      children: "Packages"
    }), "\n", _jsxs(Cards, {
      children: [_jsx(Card, {
        title: "create-turbo",
        href: "/docs/reference/create-turbo",
        description: "Get started with Turborepo using an example."
      }), _jsx(Card, {
        title: "eslint-config-turbo",
        href: "/docs/reference/eslint-config-turbo",
        description: "Find environment variables not listed in `turbo.json`."
      }), _jsx(Card, {
        title: "turbo-ignore",
        href: "/docs/reference/turbo-ignore",
        description: "Skip work using `turbo-ignore`."
      }), _jsx(Card, {
        title: "@turbo/gen",
        href: "/docs/reference/turbo-gen",
        description: "Type definitions for Turborepo generators."
      })]
    }), "\n", _jsx(_components.h2, {
      id: "flag-syntax",
      children: "Flag syntax"
    }), "\n", _jsx(_components.p, {
      children: "Options that require a value can be passed with an equals sign, using quotes when spaces are needed."
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "Terminal",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"m 4,4 a 1,1 0 0 0 -0.7070312,0.2929687 1,1 0 0 0 0,1.4140625 L 8.5859375,11 3.2929688,16.292969 a 1,1 0 0 0 0,1.414062 1,1 0 0 0 1.4140624,0 l 5.9999998,-6 a 1.0001,1.0001 0 0 0 0,-1.414062 L 4.7070312,4.2929687 A 1,1 0 0 0 4,4 Z m 8,14 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 8 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "--opt"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: "value"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "--opt"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "="
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"value with a space\""
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "--opt"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " value"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "--opt"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"value with a space\""
            })]
          })]
        })
      })
    }), "\n", _jsx(_components.h2, {
      id: "global-flags",
      children: "Global flags"
    }), "\n", _jsx(_components.h3, {
      id: "--color",
      children: _jsx(_components.code, {
        children: "--color"
      })
    }), "\n", _jsx(_components.p, {
      children: "Forces the use of color, even in non-interactive terminals. This is useful for enabling color output in CI environments like GitHub Actions that have support for rendering color."
    }), "\n", _jsx(_components.h3, {
      id: "--no-color",
      children: _jsx(_components.code, {
        children: "--no-color"
      })
    }), "\n", _jsx(_components.p, {
      children: "Suppresses color in terminal output, even in interactive terminals."
    }), "\n", _jsx(_components.h3, {
      id: "--no-update-notifier",
      children: _jsx(_components.code, {
        children: "--no-update-notifier"
      })
    }), "\n", _jsx(_components.p, {
      children: "Disables the update notification. This notification will be automatically disabled when running in CI environments, but can also be disabled manually via this flag."
    }), "\n", _jsxs(_components.p, {
      children: ["Alternatively, you can disable the notification using ", _jsxs(_components.a, {
        href: "/docs/reference/system-environment-variables",
        children: ["the ", _jsx(_components.code, {
          children: "TURBO_NO_UPDATE_NOTIFIER"
        }), " environment variable"]
      }), "."]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
